// src/components/BuscadorClases.jsx
// ══════════════════════════════════════════════════════════════════════════════
//  BUSCAR UNA CLASE — en español o en inglés
// ══════════════════════════════════════════════════════════════════════════════
//  El alumno piensa en español y los títulos están en inglés. Buscar
//  "comparativos" tiene que encontrar "Comparatives & Superlatives", o el
//  buscador parece roto justo cuando más falta hace.
//
//  ── NO SACA AL ALUMNO DE LA ESCALERA ─────────────────────────────────────────
//  Esto no navega a ningún sitio: dice DÓNDE está la clase, hace scroll hasta
//  ella y la señala en verde. El curso es una progresión A1→C2, y ver el peldaño
//  en su sitio enseña más que aterrizar en él de la nada — un A1 que busca
//  "conditionals" y cae en C1 rebota y se va.
//
//  ── VERDE Y AMARILLO SIGNIFICAN COSAS DISTINTAS ──────────────────────────────
//  Amarillo: lo que tu examen marcó. Verde: lo que acabas de buscar. Por eso el
//  verde se APAGA solo a los pocos segundos — es una respuesta a una pregunta
//  que ya hiciste, no un estado de la lección.
// ══════════════════════════════════════════════════════════════════════════════

import { useEffect, useRef, useState } from 'react';
import './BuscadorClases.css';

const ESPERA_MS = 350;      // no se busca en cada tecla: se espera a que pare
const MINIMO = 3;

export default function BuscadorClases({ onEncontrada }) {
    const [texto, setTexto] = useState('');
    const [resultados, setResultados] = useState([]);
    const [buscando, setBuscando] = useState(false);
    const [buscado, setBuscado] = useState(false);
    const temporizador = useRef(null);

    useEffect(() => {
        clearTimeout(temporizador.current);
        const q = texto.trim();
        if (q.length < MINIMO) {
            setResultados([]);
            setBuscado(false);
            return;
        }
        setBuscando(true);
        temporizador.current = setTimeout(async () => {
            try {
                const token = localStorage.getItem('token');
                const r = await fetch(
                    `/api/ruta/buscar?q=${encodeURIComponent(q)}`,
                    { headers: { Authorization: `Bearer ${token}` } });
                const d = r.ok ? await r.json() : { resultados: [] };
                setResultados(d.resultados || []);
            } catch {
                // Sin red no se enseña un error: se enseña que no hay resultados.
                // El catálogo sigue debajo y el alumno puede seguir usándolo.
                setResultados([]);
            } finally {
                setBuscando(false);
                setBuscado(true);
            }
        }, ESPERA_MS);

        return () => clearTimeout(temporizador.current);
    }, [texto]);

    const ir = (r) => {
        onEncontrada?.(r);
        setTexto('');
        setResultados([]);
        setBuscado(false);
    };

    return (
        <div className="bc-caja">
            <div className="bc-campo">
                <span className="bc-lupa" aria-hidden="true">🔎</span>
                <input
                    type="search"
                    className="bc-input"
                    value={texto}
                    onChange={(e) => setTexto(e.target.value)}
                    maxLength={60}
                    placeholder="Busca una clase — en español o inglés"
                    aria-label="Buscar una clase por su nombre"
                />
                {texto && (
                    <button className="bc-limpiar" onClick={() => setTexto('')}
                        aria-label="Borrar la búsqueda">×</button>
                )}
            </div>

            {buscando && <p className="bc-estado">Buscando…</p>}

            {!buscando && buscado && resultados.length === 0 && (
                <p className="bc-estado">
                    No encontré esa clase. Prueba con otra palabra.
                </p>
            )}

            {!buscando && resultados.length > 0 && (
                <div className="bc-hallazgos">
                    <p className="bc-estado bc-estado-ok">
                        {resultados.length === 1
                            ? 'Clase encontrada'
                            : `${resultados.length} clases encontradas`}
                    </p>
                    {resultados.map((r) => (
                        <button key={r.lesson_id} className="bc-hallazgo"
                            onClick={() => ir(r)}>
                            <span className="bc-nivel">{r.nivel}</span>
                            <span className="bc-titulo">{r.titulo}</span>
                            <span className="bc-flecha">↓</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
